import DashboardCard from '@/components/dashboard/dashboard-card'
import { DashboardShell } from '@/components/dashboard/dashboard-shell'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import { useTRPC } from '@/trpc/react'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { CalendarIcon, ClockIcon, Megaphone, TrendingUp, UsersIcon } from 'lucide-react'
import { DateTime } from 'luxon'
import { useState } from 'react'
import { Area, AreaChart, CartesianGrid, Line, LineChart, XAxis } from 'recharts'

export const Route = createFileRoute('/dashboard/admin/')({
  component: RouteComponent,
})

// Minimal chart config for ChartContainer
const chartConfig = {
  count: {
    label: 'Timesheets',
    // color: 'hsl(var(--chart-1))',
    // color: 'hsl(217, 91%, 60%)', // Blue-500 equivalent
    color: 'hsl(217, 91%, 45%)', // Blue-600 equivalent
  },
} as const

function RouteComponent() {
  const api = useTRPC()
  const [timeRange, setTimeRange] = useState('90d')
  const [employeeTrendsTimeRange, setEmployeeTrendsTimeRange] = useState(12)

  // Calculate date range based on timeRange
  const now = DateTime.now()
  const days = timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : 90
  const from = now.minus({ days }).toFormat('yyyy-MM-dd')
  const to = now.toFormat('yyyy-MM-dd')

  const { data: currentTime } = useQuery({
    queryKey: ['current-time'],
    queryFn: () => DateTime.now(),
    refetchInterval: 60000, // refetch every minute (60 seconds)
    staleTime: 0,
  })

  const { data: currentUser } = useQuery({
    ...api.user.getUserWithClientId.queryOptions(),
  })

  const {
    data: timesheetsPerDay,
    isPending,
    error,
  } = useQuery({
    ...api.timesheet.getNumTimesheetsPerDayInDateRange.queryOptions({ from, to }),
  })

  const { data: pendingVerificationCount } = useQuery({
    ...api.employee.getPendingVerificationCount.queryOptions(),
  })

  const { data: pendingApprovalCount } = useQuery({
    ...api.timesheet.getPendingApprovalCount.queryOptions(),
  })

  const { data: clientsCount } = useQuery({
    ...api.client.getClientsCount.queryOptions(),
  })

  const {
    data: employeeTrends,
    isPending: isEmployeeTrendsPending,
    error: employeeTrendsError,
  } = useQuery({
    ...api.employee.getEmployeeTrendsPerMonth.queryOptions({ months: employeeTrendsTimeRange }),
  })

  // Defensive: ensure timesheetsPerDay is an array
  const isArray = Array.isArray(timesheetsPerDay)
  type TimesheetDay = { date: string; count: number }
  const chartData: TimesheetDay[] = []
  for (let i = days - 1; i >= 0; i--) {
    const date = now.minus({ days: i }).toFormat('yyyy-MM-dd')
    const found = isArray
      ? (timesheetsPerDay as TimesheetDay[]).find((d: TimesheetDay) => d.date === date)
      : undefined
    chartData.push({ date, count: found ? found.count : 0 })
  }

  // Process employee trends data for chart
  const employeeTrendsChartData = Array.isArray(employeeTrends)
    ? employeeTrends
        .map((trend) => ({
          month: DateTime.fromISO(`${trend.month}-01`).toFormat('MMM yyyy'),
          monthSort: trend.month,
          newEmployees: trend.newEmployees,
          resignedEmployees: trend.resignedEmployees,
        }))
        .sort((a, b) => a.monthSort.localeCompare(b.monthSort))
    : []

  // // Chart configs
  // const timesheetsChartConfig = {
  //   count: {
  //     label: 'Timesheets',
  //     color: 'var(--chart-1)',
  //   },
  // } as const

  const employeeTrendsChartConfig = {
    newEmployees: {
      label: 'New Employees',
      // color: 'hsl(var(--chart-1))',
      color: 'hsl(217, 91%, 60%)', // Blue-500 equivalent
    },
    resignedEmployees: {
      label: 'Resigned Employees',
      // color: 'hsl(var(--chart-2))',
      color: 'hsl(217, 91%, 45%)', // Blue-600 equivalent (darker blue)
    },
  } as const

  return (
    <DashboardShell>
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 ">
        <div className="flex flex-col gap-2">
          <h2 className="text-2xl md:text-3xl font-bold tracking-tight bg-gradient-to-r from-gray-900 via-indigo-900 to-purple-900 dark:from-white dark:via-indigo-200 dark:to-purple-200 bg-clip-text text-transparent">
            Dashboard
          </h2>
          {currentUser?.employee?.firstName && (
            <p className="text-lg text-muted-foreground">
              Welcome, {currentUser.employee.firstName}
              {currentUser.employee.lastName && ` ${currentUser.employee.lastName}`}
            </p>
          )}
        </div>
        <div className="flex items-center space-x-2 px-4 py-2 rounded-xl bg-white/80 dark:bg-gray-900/80 backdrop-blur-lg border border-gray-200 dark:border-gray-800 shadow-lg">
          <CalendarIcon className="mr-2 h-4 w-4 text-primary" />
          <span className="text-sm text-muted-foreground">{currentTime?.toFormat('EEEE')}</span>
        </div>
      </div>
      <Separator />

      {/* Dashboard Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <DashboardCard
          description="Pending Verification"
          title="Employees"
          topRight={
            <Badge variant="outline" className="flex gap-1 rounded-lg text-xs">
              <UsersIcon className="size-3" />
              {pendingVerificationCount ?? 0}
            </Badge>
          }
          footer={
            <div className="flex flex-col w-full gap-y-2 justify-between">
              <div className="text-muted-foreground">
                <span className="font-medium">{pendingVerificationCount ?? 0}</span> employees
                pending verification
              </div>
              <Button className="w-full cursor-pointer" asChild>
                <Link to="/dashboard/admin/employees">View All</Link>
              </Button>
            </div>
          }
        />

        <DashboardCard
          description="Pending Approval"
          title="Timesheets"
          topRight={
            <Badge variant="outline" className="flex gap-1 rounded-lg text-xs">
              <ClockIcon className="size-3" />
              {pendingApprovalCount ?? 0}
            </Badge>
          }
          footer={
            <div className="flex flex-col w-full gap-y-2 justify-between">
              <div className="text-muted-foreground">
                <span className="font-medium">{pendingApprovalCount ?? 0}</span> timesheets pending
                approval
              </div>
              <Button className="w-full cursor-pointer" asChild>
                <Link to="/dashboard/admin/timesheets">View All</Link>
              </Button>
            </div>
          }
        />

        <DashboardCard
          description="Total Clients"
          title="Clients"
          topRight={
            <Badge variant="outline" className="flex gap-1 rounded-lg text-xs">
              <UsersIcon className="size-3" />
              {clientsCount ?? 0}
            </Badge>
          }
          footer={
            <div className="flex flex-col w-full gap-y-2 justify-between">
              <div className="text-muted-foreground">
                <span className="font-medium">{clientsCount ?? 0}</span> active clients
              </div>
              <Button className="w-full cursor-pointer" asChild>
                <Link to="/dashboard/admin/clients">Manage Clients</Link>
              </Button>
            </div>
          }
        />

        <DashboardCard
          description="View Errors"
          title="Errors"
          topRight={
            <Badge variant="outline" className="flex gap-1 rounded-lg text-xs">
              <Megaphone className="size-3" />
            </Badge>
          }
          footer={
            <Button className="w-full cursor-pointer" asChild>
              <Link to="/dashboard/admin/errors">View Errors</Link>
            </Button>
          }
        />
      </div>

      {/* Timesheets Per Day Chart */}
      <Card>
        <CardHeader className="flex items-center gap-2 space-y-0 border-b py-5 sm:flex-row">
          <div className="grid flex-1 gap-1 text-center sm:text-left">
            <CardTitle>Timesheets Per Day</CardTitle>
            <CardDescription>
              Showing number of timesheets per day for the last{' '}
              {timeRange === '7d' ? '7 days' : timeRange === '30d' ? '30 days' : '3 months'}
            </CardDescription>
          </div>
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-[160px] rounded-lg sm:ml-auto" aria-label="Select a value">
              <SelectValue placeholder="Last 3 months" />
            </SelectTrigger>
            <SelectContent className="rounded-xl">
              <SelectItem value="90d" className="rounded-lg">
                Last 3 months
              </SelectItem>
              <SelectItem value="30d" className="rounded-lg">
                Last 30 days
              </SelectItem>
              <SelectItem value="7d" className="rounded-lg">
                Last 7 days
              </SelectItem>
            </SelectContent>
          </Select>
        </CardHeader>
        <CardContent className="px-2 pt-4 sm:px-6 sm:pt-6">
          {isPending ? (
            <div className="flex h-[250px] items-center justify-center">
              <span className="text-muted-foreground">Loading timesheet data...</span>
            </div>
          ) : error ? (
            <div className="flex h-[250px] items-center justify-center">
              <span className="text-destructive">Failed to load data</span>
            </div>
          ) : !isArray ? (
            <div className="flex h-[250px] items-center justify-center">
              <span className="text-destructive">No data available</span>
            </div>
          ) : (
            <ChartContainer config={chartConfig} className="aspect-auto h-[250px] w-full">
              <AreaChart data={chartData} margin={{ left: 0, right: 0, top: 10, bottom: 0 }}>
                <defs>
                  <linearGradient id="fillCount" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="var(--color-count)" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="var(--color-count)" stopOpacity={0.1} />
                  </linearGradient>
                </defs>
                <CartesianGrid vertical={false} />
                <XAxis
                  dataKey="date"
                  tickLine={false}
                  axisLine={false}
                  tickMargin={8}
                  minTickGap={32}
                  tickFormatter={(value) => {
                    return DateTime.fromISO(value).toLocaleString({
                      month: 'short',
                      day: 'numeric',
                    })
                  }}
                />
                <ChartTooltip
                  cursor={false}
                  content={
                    <ChartTooltipContent
                      labelFormatter={(value) =>
                        DateTime.fromISO(value).toLocaleString({ month: 'short', day: 'numeric' })
                      }
                      indicator="dot"
                    />
                  }
                />
                <Area
                  dataKey="count"
                  type="natural"
                  fill="url(#fillCount)"
                  stroke="var(--color-count)"
                  strokeWidth={2}
                />
              </AreaChart>
            </ChartContainer>
          )}
        </CardContent>
      </Card>

      {/* Employee Trends Chart */}
      <Card>
        <CardHeader className="flex items-center gap-2 space-y-0 border-b py-5 sm:flex-row">
          <div className="grid flex-1 gap-1 text-center sm:text-left">
            <CardTitle>Employee Trends</CardTitle>
            <CardDescription>
              New hires and resignations over the last{' '}
              {employeeTrendsTimeRange === 3
                ? '3 months'
                : employeeTrendsTimeRange === 6
                  ? '6 months'
                  : '12 months'}
            </CardDescription>
          </div>
          <Select
            value={employeeTrendsTimeRange.toString()}
            onValueChange={(value) => setEmployeeTrendsTimeRange(Number(value))}
          >
            <SelectTrigger
              className="w-[160px] rounded-lg sm:ml-auto"
              aria-label="Select time range"
            >
              <SelectValue placeholder="Last 12 months" />
            </SelectTrigger>
            <SelectContent className="rounded-xl">
              <SelectItem value="12" className="rounded-lg">
                Last 12 months
              </SelectItem>
              <SelectItem value="6" className="rounded-lg">
                Last 6 months
              </SelectItem>
              <SelectItem value="3" className="rounded-lg">
                Last 3 months
              </SelectItem>
            </SelectContent>
          </Select>
        </CardHeader>
        <CardContent className="px-2 pt-4 sm:px-6 sm:pt-6">
          {isEmployeeTrendsPending ? (
            <div className="flex h-[250px] items-center justify-center">
              <span className="text-muted-foreground">Loading employee trends...</span>
            </div>
          ) : employeeTrendsError ? (
            <div className="flex h-[250px] items-center justify-center">
              <span className="text-destructive">Failed to load employee trends</span>
            </div>
          ) : !Array.isArray(employeeTrends) || employeeTrends.length === 0 ? (
            <div className="flex h-[250px] items-center justify-center">
              <span className="text-muted-foreground">No employee data available</span>
            </div>
          ) : (
            <ChartContainer
              config={employeeTrendsChartConfig}
              className="aspect-auto h-[250px] w-full"
            >
              <LineChart
                data={employeeTrendsChartData}
                margin={{ left: 12, right: 12, top: 10, bottom: 0 }}
              >
                <CartesianGrid vertical={false} />
                <XAxis
                  dataKey="month"
                  tickLine={false}
                  axisLine={false}
                  tickMargin={8}
                  tickFormatter={(value) => value.slice(0, 3)}
                />
                <ChartTooltip cursor={false} content={<ChartTooltipContent />} />
                <Line
                  dataKey="newEmployees"
                  type="monotone"
                  stroke="var(--color-newEmployees)"
                  strokeWidth={2}
                  dot={false}
                />
                <Line
                  dataKey="resignedEmployees"
                  type="monotone"
                  stroke="var(--color-resignedEmployees)"
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ChartContainer>
          )}
        </CardContent>
        <CardContent className="pt-0">
          <div className="flex w-full items-start gap-2 text-sm">
            <div className="grid gap-2">
              <div className="flex items-center gap-2 leading-none font-medium">
                Employee movement trends <TrendingUp className="h-4 w-4" />
              </div>
              <div className="text-muted-foreground flex items-center gap-2 leading-none">
                Tracking new hires vs resignations over time
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </DashboardShell>
  )
}
